import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef } from 'react';
import styled from 'styled-components';

let openModalCount = 0;
let bodyOverflowBeforeFirstModal = '';

const ModalOverlay = styled.div<{ $fullScreen: boolean; $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $fullScreen }) => ($fullScreen ? '0' : '20px')};
  background: rgba(11, 12, 12, 0.65);
`;

const Modal = styled.div<{ $fullScreen: boolean; $width: string }>`
  width: ${({ $fullScreen, $width }) => ($fullScreen ? '100vw' : $width)};
  height: ${({ $fullScreen }) => ($fullScreen ? '100vh' : 'auto')};
  max-height: ${({ $fullScreen }) => ($fullScreen ? '100vh' : 'calc(100vh - 40px)')};
  box-sizing: border-box;
  overflow: auto;
  padding: 30px;
  border: ${({ $fullScreen }) => ($fullScreen ? '0' : '4px solid #0b0c0c')};
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(11, 12, 12, 0.35);

  .govuk-button-group,
  .govuk-button {
    margin-bottom: 0;
  }

  @media (max-width: 640px) {
    padding: 20px;
  }
`;

const getFocusableElements = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('hidden'));

type Props = {
  title: string;
  children: ReactNode;
  onClose: () => void;
  width?: string;
  fullScreen?: boolean;
  zIndex?: number;
};

export default function BulkTableModal({
  title,
  children,
  onClose,
  width = 'min(100%, 520px)',
  fullScreen = false,
  zIndex = 1000,
}: Props) {
  const titleId = useId();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const returnFocusTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (openModalCount === 0) bodyOverflowBeforeFirstModal = document.body.style.overflow;
    openModalCount += 1;
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();
    return () => {
      openModalCount -= 1;
      if (openModalCount === 0) document.body.style.overflow = bodyOverflowBeforeFirstModal;
      returnFocusTo?.focus();
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !modalRef.current) return;
    const focusableElements = getFocusableElements(modalRef.current);
    if (focusableElements.length === 0) {
      event.preventDefault();
      modalRef.current.focus();
      return;
    }
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <ModalOverlay $fullScreen={fullScreen} $zIndex={zIndex}>
      <Modal
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        $fullScreen={fullScreen}
        $width={width}
        onKeyDown={handleKeyDown}
      >
        <h2 className="govuk-heading-l" id={titleId}>
          {title}
        </h2>
        {children}
      </Modal>
    </ModalOverlay>
  );
}
